import { useState } from "react";
import { useForm } from "react-hook-form";
import type { Error as LoginError } from "@auth0/auth0-acul-js";

import { butterfieldLogo } from "@/assets/logos";
import GoogleLogo from "@/components/GoogleLogo";
import { useLoginManager } from "../hooks/useLoginManager";
import { getFieldError } from "../utils/helpers/errorUtils";
import { getIdentifierDetails } from "../utils/helpers/identifierUtils";
import { rebaseLinkToCurrentOrigin } from "../utils/helpers/urlUtils";

// const heroImageUrl =
//   "https://media.butterfield.com/page-image_imageUrl_a470c5db-2106-4964-a8f2-414c0415efe6_1744290400564.jpg";
const heroImageUrl =
  "https://cdn.mediavalet.com/cato/butterfield/aEeF6_P6lk6o6VqdMg_3cQ/GX_bog2pTkawcjy_quukQA/Large/DSC09355-slovenia-ivan-sardi-2026.jpeg?f=auto";

interface LoginFormData {
  username: string;
  password: string;
  captcha?: string;
}

function LoginScreen() {
  const {
    handleLogin,
    handleFederatedLogin,
    errors,
    isCaptchaAvailable,
    captchaImage,
    resetPasswordLink,
    isForgotPasswordEnabled,
    texts,
    allowedIdentifiers,
    passwordPolicy,
    isSignupEnabled,
    signupLink,
  } = useLoginManager();

  const form = useForm<LoginFormData>({
    defaultValues: {
      username: "",
      password: "",
      captcha: "",
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors, isSubmitting },
  } = form;

  const sdkErrors = (errors || []) as LoginError[];
  const generalErrors = sdkErrors.filter((err) => !err.field);
  const usernameSDKError =
    getFieldError("username", sdkErrors) ?? getFieldError("email", sdkErrors);
  const passwordSDKError = getFieldError("password", sdkErrors);
  const captchaSDKError = getFieldError("captcha", sdkErrors);

  const [showPassword, setShowPassword] = useState(false);

  const onSubmit = async (data: LoginFormData) => {
    await handleLogin(data.username, data.password, data.captcha);
  };

  const {
    label: usernameLabel,
    type: usernameType,
    autoComplete: usernameAutoComplete,
  } = getIdentifierDetails(allowedIdentifiers, texts);
  const passwordLabel = (texts?.passwordPlaceholder || "Password").concat("*");
  const buttonText = texts?.buttonText || "Continue";
  const forgotPasswordText = texts?.forgotPasswordText || "Forgot Password?";
  const captchaLabel = (texts?.captchaCodePlaceholder || "CAPTCHA").concat("*");
  const passwordPlaceholder = texts?.passwordPlaceholder || "********";
  const usernamePlaceholder =
    texts?.usernameOrEmailPlaceholder || "you@company.com";
  const captchaPlaceholder =
    texts?.captchaCodePlaceholder || "Enter the characters you see";
  const localizedResetPasswordLink =
    resetPasswordLink && rebaseLinkToCurrentOrigin(resetPasswordLink);
  const localizedSignupLink =
    signupLink && rebaseLinkToCurrentOrigin(signupLink);

  return (
    <div className="overflow-hidden rounded-none bg-white shadow-[0_30px_80px_rgba(2,6,23,0.35)] min-h-screen">
      <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
        <div className="relative order-1 h-56 sm:h-72 md:order-2 md:block md:h-full">
          <img
            src={heroImageUrl}
            alt="Auth0 experience preview"
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-900/10 via-transparent to-slate-900/30" />
        </div>
        <form
          className="flex h-full flex-col justify-center gap-6 order-2 bg-white px-10 py-12 md:order-1 md:px-12"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="mb-0 flex flex-col items-start">
            <img
              src={butterfieldLogo}
              alt="Butterfield logo"
              className="h-14 w-auto"
              loading="lazy"
            />
            <p className="mt-4 font-display text-xl font-normal text-ink">
              Log in to continue your B&R experience.
            </p>
          </div>

          {generalErrors.length > 0 && (
            <div className="rounded-none border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger">
              <ul className="list-disc space-y-1 pl-5">
                {generalErrors.map((error, index) => (
                  <li key={`general-error-${index}`}>{error.message}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-2">
            <label
              className="text-sm font-semibold text-ink-2"
              htmlFor="username"
            >
              {usernameLabel}
            </label>
            <input
              id="username"
              type={usernameType}
              autoComplete={usernameAutoComplete}
              placeholder={usernamePlaceholder}
              className="input-field"
              aria-invalid={
                !!formErrors.username || !!usernameSDKError || undefined
              }
              {...register("username", {
                required: "This field is required",
                maxLength: {
                  value: 100,
                  message: "Maximum 100 characters allowed",
                },
              })}
            />
            {(formErrors.username?.message || usernameSDKError) && (
              <p className="text-sm text-danger">
                {formErrors.username?.message || usernameSDKError}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <label
                className="font-semibold text-ink-2"
                htmlFor="password"
              >
                {passwordLabel}
              </label>
              {isForgotPasswordEnabled && localizedResetPasswordLink ? (
                <a
                  href={localizedResetPasswordLink}
                  className="text-ink font-medium hover:text-ink-2"
                >
                  {forgotPasswordText}
                </a>
              ) : null}
            </div>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder={passwordPlaceholder}
                autoComplete="current-password"
                className="input-field pr-12 tracking-[0.3em]"
                aria-invalid={
                  !!formErrors.password || !!passwordSDKError || undefined
                }
                {...register("password", {
                  required: "Password is required",
                  maxLength: {
                    value: 200,
                    message: "Maximum 200 characters allowed",
                  },
                  minLength: passwordPolicy?.minLength
                    ? {
                        value: passwordPolicy.minLength,
                        message: `Password must be at least ${passwordPolicy.minLength} characters`,
                      }
                    : undefined,
                })}
              />
              <button
                type="button"
                aria-pressed={showPassword}
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute inset-y-0 right-3 text-xs font-semibold text-ink-2 hover:text-ink"
              >
                {showPassword ? "Hide" : "Show"}
                <span className="sr-only"> password</span>
              </button>
            </div>
            {(formErrors.password?.message || passwordSDKError) && (
              <p className="text-sm text-danger">
                {formErrors.password?.message || passwordSDKError}
              </p>
            )}
          </div>

          {isCaptchaAvailable && (
            <div className="space-y-3">
              <label
                htmlFor="captcha"
                className="text-sm font-semibold text-ink-2"
              >
                {captchaLabel}
              </label>
              {captchaImage && (
                <img
                  src={captchaImage}
                  alt="CAPTCHA challenge"
                  className="h-16 w-full rounded-none border border-border object-contain"
                />
              )}
              <input
                id="captcha"
                type="text"
                placeholder={captchaPlaceholder}
                className="input-field"
                aria-invalid={
                  !!formErrors.captcha || !!captchaSDKError || undefined
                }
                {...register("captcha", {
                  required: "Please complete the CAPTCHA",
                  maxLength: {
                    value: 15,
                    message: "CAPTCHA code is too long",
                  },
                })}
              />
              {(formErrors.captcha?.message || captchaSDKError) && (
                <p className="text-sm text-danger">
                  {formErrors.captcha?.message || captchaSDKError}
                </p>
              )}
            </div>
          )}

          {isSignupEnabled && localizedSignupLink ? (
            <p className="text-sm text-ink-2">
              {texts?.signupActionText || "Don't have an account?"}{" "}
              <a
                href={localizedSignupLink}
                className="font-semibold text-ink underline underline-offset-2"
              >
                {texts?.signupActionLinkText || "Sign up"}
              </a>
              .
            </p>
          ) : null}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-sm border border-accent bg-accent px-4 py-3 text-base font-semibold uppercase tracking-[0.1em] text-on-accent transition hover:cursor-pointer hover:border-accent-hover hover:bg-accent-hover hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? "Please wait..." : buttonText}
          </button>

          <p className="text-sm text-ink-2">
            Problems signing in?{" "}
            <a
              href="https://www.butterfield.com/my-account-faq"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-ink-2 underline underline-offset-2 hover:cursor-pointer hover:text-ink"
            >
              Click here
            </a>
            .
          </p>

          <div className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.4em] text-ink-3">
            <span className="h-px flex-1 bg-border" aria-hidden />
            <span>or</span>
            <span className="h-px flex-1 bg-border" aria-hidden />
          </div>

          <button
            type="button"
            onClick={() => handleFederatedLogin("google-oauth2")}
            className="flex w-full items-center justify-center gap-3 rounded-sm border border-border bg-white px-4 py-3 text-base font-semibold text-ink transition hover:cursor-pointer hover:bg-btn-hover hover:shadow-sm"
          >
            <span className="inline-flex items-center justify-center rounded-full bg-white">
              <GoogleLogo className="h-5 w-5" />
            </span>
            Continue with Google
          </button>
        </form>
      </div>
    </div>
  );
}

export default LoginScreen;
